/**
 * MistyBackground — the branded animated "mist" base layer for the Client Hub.
 *
 * Ported verbatim (logic-wise) from examples/conceptual_framework.html's hero canvas:
 *   - horizon gradient (#041518 → #07272D → #0a3540 → #07272D) that breathes
 *   - 3 layers of rising, wobbling, growing mist particles
 *   - 3 subtle sine waves at the horizon
 *
 * Self-contained canvas effect with full cleanup on unmount (SketchPage pattern):
 * cancels the RAF and removes the resize listener so nothing leaks.
 *
 * position: fixed; inset: 0; z-index: 0; pointer-events: none — entries sit above it.
 */

import { useEffect, useRef } from 'react'

export default function MistyBackground() {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    let W, H, particles, time
    let rafId = 0

    function resize() { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight }

    function Particle() { this.reset() }
    Particle.prototype.reset = function () {
      this.x = Math.random() * W
      this.y = H * 0.42 + Math.random() * H * 0.45
      this.vy = -(Math.random() * 0.38 + 0.14)
      this.vx = (Math.random() - 0.44) * 0.28
      this.wobbleFreq = 0.012 + Math.random() * 0.018
      this.wobbleAmp = 0.6 + Math.random() * 1.4
      this.wobbleOffset = Math.random() * Math.PI * 2
      this.life = 0
      this.maxLife = 220 + Math.random() * 280
      this.size = 55 + Math.random() * 200
      this.growRate = 0.12 + Math.random() * 0.18
      this.layer = Math.floor(Math.random() * 3)
    }
    Particle.prototype.update = function () {
      this.x += this.vx + Math.sin(this.life * this.wobbleFreq + this.wobbleOffset) * this.wobbleAmp * 0.04
      this.y += this.vy
      this.vy *= 0.9985
      this.life++
      if (this.life > this.maxLife || this.y < -this.size * 2) this.reset()
    }
    Particle.prototype.draw = function () {
      const t = this.life / this.maxLife
      const peak = 0.07 + (this.layer === 0 ? 0.02 : 0)
      const alpha = t < 0.12 ? (t / 0.12) * peak : t > 0.65 ? ((1 - t) / 0.35) * peak : peak
      const currentSize = this.size + this.life * this.growRate
      const colors = [[195, 220, 238], [150, 192, 220], [108, 162, 200]]
      const col = colors[this.layer]
      const grad = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, currentSize)
      grad.addColorStop(0, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',' + alpha + ')')
      grad.addColorStop(0.5, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',' + (alpha * 0.4) + ')')
      grad.addColorStop(1, 'rgba(' + col[0] + ',' + col[1] + ',' + col[2] + ',0)')
      ctx.beginPath()
      ctx.arc(this.x, this.y, currentSize, 0, Math.PI * 2)
      ctx.fillStyle = grad
      ctx.fill()
    }

    function drawHorizon() {
      const t = (Math.sin(time * 0.0004) + 1) / 2
      const horizonY = H * (0.48 + t * 0.06)
      const grad = ctx.createLinearGradient(0, 0, 0, H)
      grad.addColorStop(0, '#041518')
      grad.addColorStop(horizonY / H * 0.6, '#07272D')
      grad.addColorStop(horizonY / H, '#0a3540')
      grad.addColorStop(1, '#07272D')
      ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H)
      const glowGrad = ctx.createRadialGradient(W * 0.5, horizonY, 0, W * 0.5, horizonY, W * 0.55)
      glowGrad.addColorStop(0, 'rgba(15,80,90,0.22)')
      glowGrad.addColorStop(0.5, 'rgba(7,50,60,0.08)')
      glowGrad.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = glowGrad; ctx.fillRect(0, 0, W, H)
    }

    function drawWaves() {
      for (let w = 0; w < 3; w++) {
        ctx.beginPath()
        const baseY = H * (0.52 + w * 0.04)
        const amp = 3 + w * 2
        const freq = 0.004 - w * 0.0008
        const phase = time * (0.0003 + w * 0.0001) + w * 1.2
        for (let x = 0; x <= W; x += 4) {
          const y = baseY + Math.sin(x * freq + phase) * amp + Math.sin(x * freq * 1.7 + phase * 0.6) * amp * 0.4
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
        }
        ctx.strokeStyle = 'rgba(100,160,200,' + (0.04 - w * 0.01) + ')'
        ctx.lineWidth = 0.5; ctx.stroke()
      }
    }

    function init() {
      resize(); time = 0; particles = []
      for (let i = 0; i < 80; i++) {
        const p = new Particle()
        p.life = Math.floor(Math.random() * p.maxLife)
        if (i < 28) { p.y = H * 0.48 + Math.random() * H * 0.18; p.size = 80 + Math.random() * 160; p.vy = -(Math.random() * 0.22 + 0.08) }
        particles.push(p)
      }
    }

    function animate() {
      rafId = requestAnimationFrame(animate)
      time++
      drawHorizon()
      drawWaves()
      for (let i = 0; i < particles.length; i++) { particles[i].update(); particles[i].draw() }
    }

    window.addEventListener('resize', resize)
    init(); animate()

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', resize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}
    />
  )
}
